import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { LOGO_URL, IMAGES, API } from "@/constants";

const LINES = ["Flavours of", "God's Own Country,", "now at Europe's table."];

export default function Hero() {
  const ref = useRef(null);
  const [heroImage, setHeroImage] = useState(IMAGES.hero);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    axios.get(`${API}/site/content`).then((r) => {
      if (r.data?.hero_image) setHeroImage(r.data.hero_image);
    }).catch(() => {});
  }, []);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden bg-black flex items-end" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img
          src={heroImage}
          alt="Kerala backwaters at golden hour"
          className="w-full h-[115%] object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/35 to-black" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-24 pt-40">
        <motion.img
          src={LOGO_URL}
          alt="KERA"
          data-testid="hero-logo"
          className="h-14 sm:h-16 w-auto mb-10 drop-shadow-2xl"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />
        <h1
          className="font-display font-semibold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.08]"
          data-testid="hero-title-masked"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-kera-gold" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.35 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          className="mt-6 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          data-testid="hero-subtext"
        >
          Carrying the coconut groves, spice gardens and slow rituals of Kerala
          to homes and kitchens across Europe.
        </motion.p>
        <motion.div
          className="mt-10 flex flex-wrap gap-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
        >
          <Link
            to="/products"
            data-testid="hero-cta-products"
            className="rounded-full bg-kera-gold text-black px-7 py-3 text-sm font-semibold tracking-wide hover:bg-kera-goldlight transition-colors duration-300"
          >
            Shop the Range
          </Link>
          <a
            href="#journey"
            data-testid="hero-cta-journey"
            className="rounded-full border border-white/40 text-white px-7 py-3 text-sm font-medium tracking-wide hover:border-kera-gold hover:text-kera-gold transition-colors duration-300"
          >
            Trace the Journey
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
